import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { describeShiftDays, shiftHours, errorWords } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { RowActions } from "@/components/app/patterns/row-actions";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { ChevronLeft, Plus } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { readState } from "@/lib/query/read-state";
import { guardPermission } from "@/lib/policy/route-guard";
import { DepartmentIcon, departmentTone } from "./-components/department-style";
import { hoursLabel, WHOLE_BUILDING, type BoardPattern } from "./-components/board-model";
import { PatternDialog } from "./-components/pattern-dialog";
import { PresetCards } from "./-components/preset-cards";
import { RenameDepartmentDialog } from "./-components/rename-department-dialog";

export const Route = createFileRoute("/_authenticated/_workforce/schedule/patterns")({
  /* Changing what shifts a building runs is the rota manager's job, at the
     building chosen; a reader lands on the rota. The API refuses the writes anyway. */
  beforeLoad: ({ context }) => guardPermission(context.me, "schedule:manage", "/schedule"),
  component: ShiftSetup,
});

const plural = (count: number, word: string) => `${count} ${count === 1 ? word : `${word}s`}`;

/** The shifts a building runs, department by department. Each is one line: its name, its hours, its
 * days. Adding one is one dialog; a whole week at once is one press on a ready-made week below. */
function ShiftSetup() {
  const { facilityId } = useFacility();
  const canManage = useCan("schedule:manage");
  const queryClient = useQueryClient();
  const buildingChosen = facilityId !== "all";
  const patterns = useQuery(
    orpc.workforce.shiftPatterns.list.queryOptions({ input: { facilityId } }),
  );
  const [editing, setEditing] = useState<{
    pattern: BoardPattern | null;
    department: string | null;
  } | null>(null);
  const [removing, setRemoving] = useState<BoardPattern | null>(null);
  const [renaming, setRenaming] = useState<{ department: string | null } | null>(null);
  const [clearing, setClearing] = useState<{ department: string | null; count: number } | null>(
    null,
  );
  const removeDepartment = useMutation(
    orpc.workforce.shiftPatterns.removeDepartment.mutationOptions({
      onSuccess: async ({ removed }) => {
        toast.success(`${plural(removed, "shift")} deleted`);
        setClearing(null);
        await queryClient.invalidateQueries({ queryKey: orpc.workforce.shiftPatterns.key() });
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const remove = useMutation(
    orpc.workforce.shiftPatterns.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Shift deleted");
        setRemoving(null);
        await queryClient.invalidateQueries({ queryKey: orpc.workforce.shiftPatterns.key() });
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const rows = patterns.data ?? [];
  const read = readState(patterns, rows.length);
  const groups = new Map<string, BoardPattern[]>();
  for (const row of rows) {
    const key = row.department ?? "";
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }
  const departments = [...groups.keys()].filter(Boolean);

  return (
    <>
      <Button asChild className="-ms-2 mb-2" size="sm" variant="ghost">
        <Link to="/schedule">
          <ChevronLeft className="size-4" />
          Back to the schedule
        </Link>
      </Button>
      <PageHeader
        title="Shift set-up"
        meta={
          read === "ready"
            ? `${plural(rows.length, "shift")} across ${plural(groups.size, "department")}. Changing one changes next week's choices, never a week already written.`
            : "The shifts this building runs, by department."
        }
        actions={
          canManage && buildingChosen ? (
            <>
              <Button
                onClick={() => setEditing({ pattern: null, department: "" })}
                size="sm"
                variant="outline"
              >
                <Plus className="size-4" />
                Add a department
              </Button>
              <Button onClick={() => setEditing({ pattern: null, department: null })} size="sm">
                <Plus className="size-4" />
                Add a shift
              </Button>
            </>
          ) : null
        }
      />

      <div className="mt-4 space-y-6">
        {!buildingChosen ? (
          <EmptyState
            title="Choose a building"
            description="Each building runs its own shifts. Pick one in the switcher at the top."
          />
        ) : read === "loading" ? (
          <Skeleton className="h-48 w-full rounded-xl" />
        ) : read === "failed" ? (
          <EmptyState
            title="Could not load the shifts"
            description={errorWords(patterns.error, "Nothing was read.")}
            action={
              <Button disabled={patterns.isFetching} onClick={() => void patterns.refetch()}>
                Try again
              </Button>
            }
          />
        ) : read === "empty" ? (
          <EmptyState
            title="No shifts yet"
            description="Start from a whole week below, or add shifts one at a time."
          />
        ) : (
          [...groups.entries()].map(([key, list], index) => {
            const tone = departmentTone(index);
            const department = key || null;
            return (
              <section
                className="bg-card overflow-hidden rounded-xl border"
                key={key || WHOLE_BUILDING}
              >
                <header
                  className={`flex items-center justify-between gap-2 border-b px-4 py-2.5 ${tone.fill}`}
                >
                  <h2 className="flex items-center gap-2 text-base font-semibold">
                    <DepartmentIcon className={`size-4 ${tone.ink}`} department={department} />
                    {department ?? WHOLE_BUILDING}
                  </h2>
                  {canManage ? (
                    <div className="flex items-center gap-1">
                      <Button
                        onClick={() => setEditing({ pattern: null, department })}
                        size="sm"
                        variant="ghost"
                      >
                        <Plus className="size-4" />
                        Add
                      </Button>
                      <RowActions
                        items={[
                          {
                            label: department ? "Rename department…" : "Move to a department…",
                            onSelect: () => setRenaming({ department }),
                          },
                          {
                            label: department ? "Delete department" : "Delete these shifts",
                            destructive: true,
                            onSelect: () => setClearing({ department, count: list.length }),
                          },
                        ]}
                        label={department ?? WHOLE_BUILDING}
                      />
                    </div>
                  ) : null}
                </header>
                <ul className="divide-y">
                  {list.map((pattern) => (
                    <li className="flex items-center gap-3 px-4 py-2.5" key={pattern.id}>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">{pattern.name}</p>
                        <p className="text-muted-foreground text-xs tabular-nums">
                          {hoursLabel(pattern.startTime, pattern.endTime)},{" "}
                          {shiftHours(pattern.startTime, pattern.endTime)} hours,{" "}
                          {pattern.breakMinutes > 0 ? `${pattern.breakMinutes} min break, ` : ""}
                          {describeShiftDays(pattern.days)}
                        </p>
                      </div>
                      {canManage ? (
                        <RowActions
                          label={pattern.name}
                          items={[
                            {
                              label: "Change…",
                              onSelect: () =>
                                setEditing({ pattern, department: pattern.department }),
                            },
                            {
                              label: "Delete shift",
                              destructive: true,
                              onSelect: () => setRemoving(pattern),
                            },
                          ]}
                        />
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })
        )}

        {canManage && buildingChosen && read !== "loading" && read !== "failed" ? (
          <section className="space-y-3">
            <div>
              <h2 className="text-base font-semibold">Start from a whole week</h2>
              <p className="text-muted-foreground text-sm">
                A whole department's shifts in one press, from a ready-made week or one you wrote.
                Shifts you already have with the same name are left alone.
              </p>
            </div>
            <PresetCards departments={departments} facilityId={facilityId} patterns={rows} />
          </section>
        ) : null}
      </div>

      <PatternDialog
        department={editing?.department ?? null}
        departments={departments}
        facilityId={facilityId}
        onClose={() => setEditing(null)}
        open={editing !== null}
        pattern={editing?.pattern ?? null}
      />
      <RenameDepartmentDialog
        facilityId={facilityId}
        onClose={() => setRenaming(null)}
        target={renaming}
      />
      {clearing ? (
        <RemoveConfirm
          confirmLabel={clearing.department ? "Delete department" : "Delete shifts"}
          description={`${clearing.department ?? "The whole building"} stops being offered on the schedule: ${plural(clearing.count, "shift")} deleted from the set-up. Weeks already written keep their shifts.`}
          onClose={() => setClearing(null)}
          onConfirm={() => removeDepartment.mutate({ facilityId, department: clearing.department })}
          title={`Delete ${clearing.department ?? "the whole building's shifts"}?`}
        />
      ) : null}
      {removing ? (
        <RemoveConfirm
          confirmLabel="Delete shift"
          description={`${removing.name} stops being offered on the schedule. Weeks already written keep their shifts.`}
          onClose={() => setRemoving(null)}
          onConfirm={() => remove.mutate({ patternId: removing.id })}
          title={`Delete ${removing.name}?`}
        />
      ) : null}
    </>
  );
}
